/* Scent Finder — Horizon section (home-redesign reference markup)
 * Vanilla, scoped, no dependencies.
 *
 * Data: the full all-dupes catalogue (facets + preview fields) is fetched from
 * a static asset (assets/scent-finder-data.json, generated by
 * Ralph/server/scripts/generate_scent_finder_data.py). Liquid can only render
 * 50 products on a homepage section, so the finder reads the whole catalogue
 * from JSON to count and narrow accurately across all ~188 products.
 *
 * Filtering is FACETED NARROWING — each question narrows the set:
 *   - gender: product must be for the chosen audience (unisex always passes)
 *   - mood / family / occasion: OR within a question, AND across questions
 *   A product is a match only if it satisfies EVERY active question.
 *
 * The grid previews the top N matches (data-results-to-show); the full set
 * lives behind the "See all matches" link (Search & Discovery filtered
 * collection, which applies the same OR-within / AND-across facet logic).
 *
 * Selectors: #fc-gender (.fg-seg), #fc-mood/#fc-family/#fc-occasion (.chips-row),
 *   #finder-count, #finder-cta, #finder-reset, #finder-results.
 */
(function () {
  'use strict';

  var GENDER_SEG = [
    { val: 'Everyone', label: 'Everyone' },
    { val: 'For her', label: 'For her' },
    { val: 'For him', label: 'For him' }
  ];

  // Storefront Search & Discovery filter keys for the active facets.
  var FILTER_KEYS = {
    family: 'filter.p.m.custom.scent_family',
    mood: 'filter.p.m.custom.mood',
    occasion: 'filter.p.m.custom.occasion'
  };

  // Gender is a standard taxonomy attribute, filtered by its global Shopify
  // TaxonomyValue gid (Female = 18, Male = 19) under the variant-taxonomy key —
  // NOT a custom metafield. Without this the "See all matches" link dropped
  // gender and the collection showed both men's and women's.
  var GENDER_FILTER_KEY = 'filter.v.t.shopify.target-gender';
  var GENDER_TAXONOMY = {
    'For her': 'gid://shopify/TaxonomyValue/18',
    'For him': 'gid://shopify/TaxonomyValue/19'
  };

  function formatMoney(cents) {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      var fmt = (window.Shopify && window.Shopify.money_format) || '${{amount}}';
      try {
        return window.Shopify.formatMoney(cents, fmt);
      } catch (e) {
        /* fall through */
      }
    }
    return '$' + (cents / 100).toFixed(2);
  }

  // Append a CDN width param; product image urls already carry ?v=…
  function sizedImg(url, w) {
    if (!url) return '';
    return url + (url.indexOf('?') > -1 ? '&' : '?') + 'width=' + w;
  }

  function uniqueSorted(values) {
    var seen = Object.create(null);
    var out = [];
    values.forEach(function (v) {
      if (v && !seen[v]) {
        seen[v] = true;
        out.push(v);
      }
    });
    return out.sort(function (a, b) { return a.localeCompare(b); });
  }

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  }

  function ScentFinderInit(root) {
    var resultsEl = root.querySelector('#finder-results');
    var countEl = root.querySelector('#finder-count');
    var ctaEl = root.querySelector('#finder-cta');
    var resetEl = root.querySelector('#finder-reset');
    var genderSegEl = root.querySelector('#fc-gender');
    var chipHosts = {
      mood: root.querySelector('#fc-mood'),
      family: root.querySelector('#fc-family'),
      occasion: root.querySelector('#fc-occasion')
    };

    if (!resultsEl) return;

    var poolUrl = root.getAttribute('data-pool-url') || '';
    var collectionUrl = root.getAttribute('data-collection-url') || '';
    var resultsToShow = parseInt(root.getAttribute('data-results-to-show'), 10) || 4;
    var emptyPrompt = root.getAttribute('data-empty-prompt') || 'Pick a vibe to see your matches';

    if (!poolUrl) return;

    fetch(poolUrl, { headers: { Accept: 'application/json' } })
      .then(function (res) {
        if (!res.ok) throw new Error('pool fetch failed: ' + res.status);
        return res.json();
      })
      .then(function (data) {
        var pool = (data && data.products) || [];
        if (!collectionUrl) collectionUrl = (data && data.collectionUrl) || '';
        build(pool);
      })
      .catch(function () {
        if (countEl) countEl.textContent = 'Scent finder is unavailable right now.';
      });

    function build(pool) {
      if (!pool.length) {
        if (countEl) countEl.textContent = 'No products available yet.';
        return;
      }

      var state = {
        gender: 'Everyone',
        mood: new Set(),
        family: new Set(),
        occasion: new Set()
      };

      // ---- Gender segmented control ----
      if (genderSegEl) {
        GENDER_SEG.forEach(function (opt, i) {
          var btn = document.createElement('button');
          btn.type = 'button';
          btn.setAttribute('role', 'radio');
          btn.setAttribute('data-val', opt.val);
          btn.setAttribute('aria-checked', i === 0 ? 'true' : 'false');
          btn.tabIndex = i === 0 ? 0 : -1;
          if (i === 0) btn.classList.add('is-on');
          btn.textContent = opt.label;
          genderSegEl.appendChild(btn);
        });
      }

      // ---- Build chips from the distinct vocab across the whole catalogue ----
      function buildChips(group) {
        var host = chipHosts[group];
        if (!host) return;
        var all = [];
        pool.forEach(function (p) { all = all.concat(p[group] || []); });
        var values = uniqueSorted(all);
        if (!values.length) {
          var q = host.closest('.finder-q');
          if (q) q.hidden = true;
          return;
        }
        values.forEach(function (val) {
          var btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'chip';
          btn.setAttribute('data-group', group);
          btn.setAttribute('data-val', val);
          btn.setAttribute('aria-pressed', 'false');
          btn.textContent = val;
          host.appendChild(btn);
        });
      }
      buildChips('mood');
      buildChips('family');
      buildChips('occasion');

      function genderPass(p) {
        if (state.gender === 'Everyone') return true;
        if (p.gender === 'unisex') return true;
        return state.gender === 'For her' ? p.gender === 'her' : p.gender === 'him';
      }

      // OR within a group, AND across groups: a match satisfies every active question.
      function groupPass(p, group) {
        if (!state[group].size) return true;
        var vals = p[group] || [];
        for (var i = 0; i < vals.length; i += 1) {
          if (state[group].has(vals[i])) return true;
        }
        return false;
      }

      function matches(p) {
        return (
          genderPass(p) &&
          groupPass(p, 'mood') &&
          groupPass(p, 'family') &&
          groupPass(p, 'occasion')
        );
      }

      function anyChips() {
        return state.mood.size || state.family.size || state.occasion.size;
      }

      // Best previews first: most-reviewed, then highest-rated.
      function byPopularity(a, b) {
        if (b.ratingCount !== a.ratingCount) return b.ratingCount - a.ratingCount;
        return (b.ratingValue || 0) - (a.ratingValue || 0);
      }

      function buildCtaUrl() {
        if (!collectionUrl) return '#';
        var params = [];
        var genderGid = GENDER_TAXONOMY[state.gender];
        if (genderGid) {
          params.push(GENDER_FILTER_KEY + '=' + encodeURIComponent(genderGid));
        }
        ['family', 'mood', 'occasion'].forEach(function (group) {
          state[group].forEach(function (v) {
            params.push(FILTER_KEYS[group] + '=' + encodeURIComponent(v));
          });
        });
        return params.length ? collectionUrl + '?' + params.join('&') : collectionUrl;
      }

      function stars(rv) {
        var rounded = Math.round(rv);
        var out = '';
        for (var i = 1; i <= 5; i += 1) out += i <= rounded ? '★' : '☆';
        return out;
      }

      function buildCard(p) {
        var card = el('article', 'card');
        card.setAttribute('data-product', '');

        var media = el('a', 'card-media');
        media.href = p.url;
        var badges = el('div', 'card-badges');
        if (p.familyLabel && p.family && p.family.length) {
          badges.appendChild(el('span', 'tag tag-family', p.family[0]));
        }
        media.appendChild(badges);
        if (p.img) {
          var img = el('img');
          img.src = sizedImg(p.img, 500);
          img.srcset = sizedImg(p.img, 300) + ' 300w, ' + sizedImg(p.img, 500) + ' 500w';
          img.sizes = '(min-width: 750px) 22vw, 45vw';
          if (p.imgW) img.width = p.imgW;
          if (p.imgH) img.height = p.imgH;
          img.alt = p.title;
          img.loading = 'lazy';
          media.appendChild(img);
        }
        card.appendChild(media);

        var body = el('div', 'card-body');
        if (p.familyLabel) body.appendChild(el('div', 'card-fam', p.familyLabel));

        var nameLink = el('a');
        nameLink.href = p.url;
        nameLink.appendChild(el('h3', 'card-name', p.title));
        body.appendChild(nameLink);

        if (p.inspired) {
          var insp = 'Inspired by ' + p.inspired + (p.house ? ' · ' + p.house : '');
          body.appendChild(el('div', 'card-insp', insp));
        } else if (p.house) {
          body.appendChild(el('div', 'card-insp', p.house));
        }

        if (p.keyNotes) body.appendChild(el('div', 'card-notes', p.keyNotes));

        if (p.ratingCount > 0) {
          var rate = el('div', 'card-rate');
          var starsEl = el('span', 'stars sm', stars(p.ratingValue));
          starsEl.setAttribute('aria-hidden', 'true');
          starsEl.style.setProperty('--rating', String(p.ratingValue));
          rate.appendChild(starsEl);
          rate.appendChild(el('span', 'visually-hidden', 'Rated ' + p.ratingValue + ' out of 5'));
          rate.appendChild(el('span', null, p.ratingValue + ' (' + p.ratingCount + ')'));
          body.appendChild(rate);
        }

        var foot = el('div', 'card-foot');
        foot.appendChild(el('div', 'price', formatMoney(p.price)));
        body.appendChild(foot);

        card.appendChild(body);
        return card;
      }

      function update() {
        var matched = pool.filter(matches);
        var total = matched.length;
        var hasChips = anyChips();

        // Render the top N previews.
        var shown = matched.slice().sort(byPopularity).slice(0, resultsToShow);
        resultsEl.textContent = '';
        shown.forEach(function (p) { resultsEl.appendChild(buildCard(p)); });

        var genderLabel =
          state.gender === 'For her' ? 'her' : state.gender === 'For him' ? 'him' : '';

        if (countEl) {
          if (hasChips) {
            countEl.textContent = total
              ? total + ' match' + (total > 1 ? 'es' : '') + ' for you'
              : 'No exact match — try fewer filters';
          } else if (state.gender !== 'Everyone') {
            countEl.textContent = total + ' scent' + (total > 1 ? 's' : '') + ' for ' + genderLabel;
          } else {
            countEl.textContent = emptyPrompt;
          }
        }

        var anySelection = hasChips || state.gender !== 'Everyone';
        if (ctaEl) {
          if (anySelection && total) {
            ctaEl.style.display = 'inline-flex';
            ctaEl.setAttribute('href', buildCtaUrl());
          } else {
            ctaEl.style.display = 'none';
          }
        }
      }

      // ---- Gender segmented control (radio semantics + arrow keys) ----
      var segButtons = genderSegEl
        ? Array.prototype.slice.call(genderSegEl.querySelectorAll('button'))
        : [];
      function selectGender(btn) {
        state.gender = btn.getAttribute('data-val');
        segButtons.forEach(function (b) {
          var on = b === btn;
          b.classList.toggle('is-on', on);
          b.setAttribute('aria-checked', on ? 'true' : 'false');
          b.tabIndex = on ? 0 : -1;
        });
        update();
      }
      if (genderSegEl) {
        genderSegEl.addEventListener('click', function (e) {
          var btn = e.target.closest('button');
          if (btn) selectGender(btn);
        });
        genderSegEl.addEventListener('keydown', function (e) {
          var idx = segButtons.indexOf(document.activeElement);
          if (idx === -1) return;
          var next = null;
          if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = segButtons[(idx + 1) % segButtons.length];
          else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = segButtons[(idx - 1 + segButtons.length) % segButtons.length];
          if (next) {
            e.preventDefault();
            next.focus();
            selectGender(next);
          }
        });
      }

      // ---- Chip toggles (delegated) ----
      root.addEventListener('click', function (e) {
        var chip = e.target.closest('.chip[data-group]');
        if (!chip) return;
        var group = chip.getAttribute('data-group');
        var val = chip.getAttribute('data-val');
        if (!state[group]) return;
        if (state[group].has(val)) {
          state[group].delete(val);
          chip.classList.remove('is-on');
          chip.setAttribute('aria-pressed', 'false');
        } else {
          state[group].add(val);
          chip.classList.add('is-on');
          chip.setAttribute('aria-pressed', 'true');
        }
        update();
      });

      // ---- Reset ----
      if (resetEl) {
        resetEl.addEventListener('click', function () {
          state.gender = 'Everyone';
          state.mood.clear();
          state.family.clear();
          state.occasion.clear();
          segButtons.forEach(function (b, i) {
            var on = i === 0;
            b.classList.toggle('is-on', on);
            b.setAttribute('aria-checked', on ? 'true' : 'false');
            b.tabIndex = on ? 0 : -1;
          });
          Array.prototype.forEach.call(root.querySelectorAll('.chip.is-on'), function (c) {
            c.classList.remove('is-on');
            c.setAttribute('aria-pressed', 'false');
          });
          update();
        });
      }

      update();
    }
  }

  function boot(scope) {
    var nodes = (scope || document).querySelectorAll('scent-finder');
    Array.prototype.forEach.call(nodes, function (node) {
      if (node.dataset.sfInit) return;
      node.dataset.sfInit = '1';
      ScentFinderInit(node);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(document); });
  } else {
    boot(document);
  }

  // Theme editor: re-init when the section is re-rendered.
  document.addEventListener('shopify:section:load', function (e) {
    boot(e.target);
  });
})();
